import PropTypes from 'prop-types'

import styled, { css } from 'styled-components'

import colors from 'components/utils/colors'

import { useState, useRef, cloneElement } from 'react'

import useOnclickOutside from 'react-cool-onclickoutside'

const DownCaret = styled.span`
  margin: 5px 0 2px 4px;
`

const StyledNavBarMenu = styled.div`
  cursor: pointer;
  ${DownCaret} {
    &:hover:before {
      color: ${colors.secondary40};
    }
  }
`

const tabUnderlineStyle = css`
  &:before {
    position: absolute;
    content: '';
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background-color: ${colors.primary35};
  }
`

const blueHeaderAndDownCaretStyle = css`
  color: ${colors.primary35};
  ${DownCaret} {
    &:before {
      color: ${colors.primary35};
    }
  }
`

export const StyledNavBarMenuTitle = styled.a`
  display: flex;
  align-items: center;
  color: ${colors.secondary40};
  font-size: 14px;
  line-height: 48px;
  position: relative;
  white-space: nowrap;
  padding: 0 12px;
  margin: 0;

  &:hover, &:focus-within {
    background-color: ${colors.primary100};
    ${blueHeaderAndDownCaretStyle};
    ${tabUnderlineStyle};
  }

  ${props => props.active && css`
    ${blueHeaderAndDownCaretStyle};
    ${tabUnderlineStyle};
  `}

  ${props => props.opened && css`
    background-color: ${colors.primaryLightest};
    ${blueHeaderAndDownCaretStyle};
    ${tabUnderlineStyle};
  `}
`

export const StyledNavBarMenuContent = styled.div`
  position: absolute;
  min-width: 124px;
  z-index: 1;
  right: auto;
  transition: all 0.25s linear 0s;

  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.29);
  background-color: #FFFFFF;

  margin-top: 1px;

  display: none;

  ${props => props.opened && css`
    display: block;
  `}
`

const NavBarMenu = ({ active, title, children }) => {
  const [opened, setOpened] = useState(false)

  // We use this to select first menu item during keyboard arrow navigation
  // See https://stackoverflow.com/a/67955503
  const firstChildRef = useRef(null)
  const menuTitleRef = useRef(null)
  const menuContentRef = useRef(null)

  const onKeyDown = (e) => {
    if (opened) {
      if (e.key === 'ArrowUp') {
        e.target.previousElementSibling?.focus()
      } else if (e.key === 'ArrowDown') {
        e.target.nextElementSibling?.focus()
      }

      if (
        e.key === 'Escape' ||
        (e.target === firstChildRef.current &&
          (e.key === 'ArrowUp' || (e.shiftKey && e.key === 'Tab'))
        )
      ) {
        setOpened(false)
        setTimeout(() => { menuTitleRef.current.focus() }, 0)
      }
    }

    // We have separate if here so those keys will work even when menu is already opened
    // This is useful when menu was opened with mouse and user starts navigating with KB after that
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      if (!opened || e.target === menuTitleRef.current) {
        setOpened(true)
        setTimeout(() => { firstChildRef.current.focus() }, 0)
        e.stopPropagation()
      }
    }
  }

  const onBlur = (e) => {
    const currentTarget = e.currentTarget
    // Check the newly focused element in the next tick of the event loop
    setTimeout(() => {
      // Check if the new activeElement is a child of the original container
      const el = document.activeElement
      // Only handle keyboard-driven focus moves here (focus lands on a real element).
      // Pointer clicks are handled by useOnclickOutside below. This avoids hiding the
      // menu content mid-click, which Safari treats as a suppressed click and breaks links.
      if (el && el !== document.body && !currentTarget.contains(el)) {
        closeAndUnfocus()
      }
    }, 0)
  }

  const closeAndUnfocus = () => {
    setOpened(false)

    // Firefox hack to remove focus styling from parent element with :focus-within selector
    // when dropdown is hidden.
    if (opened && menuContentRef.current?.contains(document.activeElement)) {
      document.activeElement.blur()
    }
  }

  // Close the menu on pointer interactions outside of it (mouse click / touch).
  const outsideClickRef = useOnclickOutside(closeAndUnfocus)

  return (
    <StyledNavBarMenu
      ref={outsideClickRef}
      onClick={() => setOpened(!opened)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      data-test='navbar_menu'
    >
      <StyledNavBarMenuTitle
        active={active}
        opened={opened}
        tabIndex={0}
        ref={menuTitleRef}
      >
        {title}
        <DownCaret className='icon-arrow-down-small-8' />
      </StyledNavBarMenuTitle>
      <StyledNavBarMenuContent
        opened={opened}
        onClick={closeAndUnfocus}
        ref={menuContentRef}
        data-test='navbar_menu_content'
      >
        {children.map((child, index) => {
          if (!child) {
            return undefined
          }
          if (index === 0) {
            return cloneElement(child, { innerRef: firstChildRef, key: index })
          }
          return cloneElement(child, { key: index })
        })}
      </StyledNavBarMenuContent>
    </StyledNavBarMenu>
  )
}

NavBarMenu.propTypes = {
  active: PropTypes.bool,
  title: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]),
  children: PropTypes.node.isRequired,
}

export default NavBarMenu
